/**
 * "Exporting client data" tour.
 *
 * Two distinct exports live on the client list:
 *   - Top-right "Export" button — opens a picker with two portfolio-wide
 *     spreadsheets (Client List, Verification & Screening Register).
 *   - Per-row download icon — a single client's full CDD record as a Word .doc.
 *
 * They look similar (both labelled with download icons) but produce very
 * different files and hit different endpoints, so users frequently confuse
 * them.
 *
 * Targets:
 *   data-tour="export-csv-btn"     — top-right "Export" button (legacy attr name kept)
 *   data-tour="export-cdd-icon"    — per-row "Export CDD Record" icon button
 *
 * Spreadsheet columns and per-client export contents mirror
 * AmlDocumentExportController.php — keep in sync if those endpoints change.
 */

import { NEXT, BACK, START, SKIP, DONE, targetExists } from './_helpers.js';

function waitForElement(selector, timeout = 10000) {
    return new Promise((resolve) => {
        if (document.querySelector(selector)) return resolve();
        const start = Date.now();
        const check = () => {
            if (document.querySelector(selector)) return resolve();
            if (Date.now() - start > timeout) return resolve();
            setTimeout(check, 50);
        };
        check();
    });
}

export const exportClientTour = {
    id: 'export-clients',
    title: 'Exporting client data',
    description: 'Two portfolio-wide spreadsheets from the Export button, plus a per-client CDD record as a Word document. When to use which.',
    autoLaunchOn: { page: 'clients' },

    steps: [
        {
            id: 'intro',
            title: 'Two places to export from',
            text: `The client list has two different download paths that do very
                different things:
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li><strong>Export</strong> (top-right of the page) — opens a window
                        with two portfolio-wide spreadsheets.</li>
                    <li><strong>Export CDD Record</strong> (download icon on each row)
                        — exports one client\'s complete CDD record as a Word document.</li>
                </ul>`,
            buttons: [START],
        },
        {
            id: 'csv',
            attachTo: { element: '[data-tour="export-csv-btn"]', on: 'left' },
            showOn: targetExists('[data-tour="export-csv-btn"]'),
            beforeShowPromise: () => waitForElement('[data-tour="export-csv-btn"]'),
            title: 'Export — pick a portfolio spreadsheet',
            text: `Opens a small window with two XLSX downloads:
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li><strong>Client List</strong> — profile snapshot for every client
                        (risk, CDD status, services, review dates, assigned staff).</li>
                    <li><strong>Verification &amp; Screening Register</strong> — identity
                        verification, PEP/sanctions results and holds, plus a per-person sheet.</li>
                </ul>
                Useful for portfolio reviews, AUSTRAC requests, or audit preparation.
                The button only appears when there is at least one client to export.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'csv-empty-note',
            showOn: () => !document.querySelector('[data-tour="export-csv-btn"]'),
            title: 'Export (currently hidden)',
            text: `The <strong>Export</strong> button only shows when there's at
                least one client on file. Once you have clients, the button will appear
                in the top-right of the page next to <em>Add Client</em>.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'cdd-icon',
            attachTo: { element: '[data-tour="export-cdd-icon"]', on: 'left' },
            showOn: targetExists('[data-tour="export-cdd-icon"]'),
            beforeShowPromise: () => waitForElement('[data-tour="export-cdd-icon"]'),
            title: 'Export CDD Record — a single client\'s full file',
            text: `The download icon on each client row is different — it generates a
                <strong>Word document</strong> with that one client\'s complete CDD record.
                <br><br>
                Includes:
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li>Client details and entity information</li>
                    <li>Designated services on file</li>
                    <li>Associated persons</li>
                    <li>Ongoing-monitoring notes</li>
                    <li>Escalation history</li>
                </ul>
                Use this when you need to hand a complete CDD file to an auditor, regulator,
                or for archival.`,
            buttons: [BACK, DONE],
        },
    ],
};
